/**
 * Product Finder Settings Panel Component
 *
 * Provides configuration options for product finder (quiz) forms:
 * - Progress bar display
 * - Email collection at end
 * - Results page configuration
 */

import { Sparkles, Mail, FileText, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { QuizConfig, defaultQuizConfig } from '@/lib/forms/templates';

interface QuizSettingsPanelProps {
  config: QuizConfig;
  onChange: (config: QuizConfig) => void;
  className?: string;
}

export function QuizSettingsPanel({
  config,
  onChange,
  className,
}: QuizSettingsPanelProps) {
  const updateConfig = (updates: Partial<QuizConfig>) => {
    onChange({ ...config, ...updates });
  };

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <CardTitle className="text-base">Product Finder Settings</CardTitle>
            <CardDescription className="text-xs">
              Configure discovery flow and results display
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Progress Display */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="progress-bar" className="text-sm font-medium">
                Show Progress Bar
              </Label>
            </div>
            <Switch
              id="progress-bar"
              checked={config.showProgressBar}
              onCheckedChange={(checked) =>
                updateConfig({ showProgressBar: checked })
              }
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Display a progress indicator as users move through questions
          </p>
        </div>

        <Separator />

        {/* Email Collection */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            Lead Capture
          </h4>

          <div className="space-y-3">
            {/* Collect Email */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="collect-email" className="text-sm">
                  Collect email before results
                </Label>
                <p className="text-xs text-muted-foreground">
                  Ask for email address before showing recommendations
                </p>
              </div>
              <Switch
                id="collect-email"
                checked={config.collectEmailAtEnd}
                onCheckedChange={(checked) =>
                  updateConfig({ collectEmailAtEnd: checked })
                }
              />
            </div>

            {/* Email Required */}
            {config.collectEmailAtEnd && (
              <div className="flex items-center justify-between ml-4 pl-4 border-l-2">
                <div>
                  <Label htmlFor="email-required" className="text-sm">
                    Email required
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Users must provide email to see results
                  </p>
                </div>
                <Switch
                  id="email-required"
                  checked={config.emailRequired}
                  onCheckedChange={(checked) =>
                    updateConfig({ emailRequired: checked })
                  }
                />
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Results Page */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Results Page
          </h4>

          <div className="space-y-3">
            {/* Show Results Page */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="show-results" className="text-sm">
                  Show results page
                </Label>
                <p className="text-xs text-muted-foreground">
                  Display a recommendation page after completion
                </p>
              </div>
              <Switch
                id="show-results"
                checked={config.showResultsPage}
                onCheckedChange={(checked) =>
                  updateConfig({ showResultsPage: checked })
                }
              />
            </div>

            {/* Results Title */}
            {config.showResultsPage && (
              <div className="space-y-2 ml-4 pl-4 border-l-2">
                <Label htmlFor="results-title" className="text-sm">
                  Results title
                </Label>
                <Input
                  id="results-title"
                  value={config.resultsTitle || ''}
                  onChange={(e) => updateConfig({ resultsTitle: e.target.value })}
                  placeholder="Your Recommendation"
                  className="h-8"
                />
              </div>
            )}

            {/* Results Description */}
            {config.showResultsPage && (
              <div className="space-y-2 ml-4 pl-4 border-l-2">
                <Label htmlFor="results-description" className="text-sm">
                  Results description
                </Label>
                <Textarea
                  id="results-description"
                  value={config.resultsDescription || ''}
                  onChange={(e) => updateConfig({ resultsDescription: e.target.value })}
                  placeholder="Based on your answers, here is what we recommend for you."
                  rows={2}
                  className="resize-none text-sm"
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Compact Product Finder Settings for the form builder sidebar
 */
interface QuizSettingsCompactProps {
  config: QuizConfig;
  onChange: (config: QuizConfig) => void;
}

export function QuizSettingsCompact({ config, onChange }: QuizSettingsCompactProps) {
  const updateConfig = (updates: Partial<QuizConfig>) => {
    onChange({ ...config, ...updates });
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Sparkles className="h-4 w-4 text-purple-500" />
        Product Finder Settings
      </div>

      <div className="space-y-3">
        {/* Progress bar */}
        <div className="flex items-center justify-between">
          <span className="text-sm">Show progress bar</span>
          <Switch
            checked={config.showProgressBar}
            onCheckedChange={(checked) =>
              updateConfig({ showProgressBar: checked })
            }
          />
        </div>

        {/* Collect email */}
        <div className="flex items-center justify-between">
          <span className="text-sm">Collect email</span>
          <Switch
            checked={config.collectEmailAtEnd}
            onCheckedChange={(checked) =>
              updateConfig({ collectEmailAtEnd: checked })
            }
          />
        </div>

        {/* Email required (nested) */}
        {config.collectEmailAtEnd && (
          <div className="flex items-center justify-between ml-4 pl-3 border-l">
            <span className="text-sm text-muted-foreground">Required</span>
            <Switch
              checked={config.emailRequired}
              onCheckedChange={(checked) =>
                updateConfig({ emailRequired: checked })
              }
            />
          </div>
        )}

        {/* Show results */}
        <div className="flex items-center justify-between">
          <span className="text-sm">Show results page</span>
          <Switch
            checked={config.showResultsPage}
            onCheckedChange={(checked) =>
              updateConfig({ showResultsPage: checked })
            }
          />
        </div>

        {/* Results title (nested) */}
        {config.showResultsPage && (
          <div className="space-y-2 ml-4 pl-3 border-l">
            <Input
              value={config.resultsTitle || ''}
              onChange={(e) => updateConfig({ resultsTitle: e.target.value })}
              placeholder="Results title..."
              className="h-7 text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );
}
